import React, { useMemo, useState } from "react";
import CustomTable from "components/Table/CustomTable";
import { useModal } from "contexts/ModalProvider";
import { useToasts } from "utils/useHotToast";
import { Dropdown, DropdownMenu, DropdownToggle, DropdownItem, Input } from "reactstrap";
import edit from "assets/icons/edit.svg";
import trash from "assets/icons/trash.svg";
import check from "assets/icons/check-circle.svg";
import slash from "assets/icons/slash-circle.svg";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { ContentsSchema } from "model/contents/contents.schema";
import ToastNotification from "components/Toast/ToastNotification";
import { useUpdateContentMutation, useDeleteContentMutation } from "services/contents/api";
import Image from "components/Common/Image";
import { getImageUrl, ImageSourceType } from "utils/imageUtils";

// Custom Hooks
import useTableChangeHandler from "hooks/useTableChangeHandler";

const ContentsTable = ({ data, onStatusChange, onTableChange, isLoading, error }) => {
  const { modal } = useModal();
  const { addToast, removeToast } = useToasts();
  const [actionsDropdown, setActionsDropdown] = useState({});

  const [updateContent, { isLoading: isUpdating }] = useUpdateContentMutation();
  const [deleteContent] = useDeleteContentMutation();

  // HOOK CHANGE
  const { handleSort, handleFilter, handleSearch, handlePageChange } = useTableChangeHandler(onTableChange);

  const columns = useMemo(
    () => [
      {
        header: "หัวข้อและหน้าปก",
        accessor: "name",
        sortable: true,
        render: (item) => (
          <div className="content-info d-flex align-items-center gap-3">
            <Image
              src={getImageUrl(item.contentImg, {
                type: ImageSourceType.LOCAL,
                path: "contents",
              })}
              alt={item.contentName}
            />
            <div className="text-lg fw-semibold">{item.contentName}</div>
          </div>
        ),
      },
      {
        header: "ระยะเวลาโปรโมชัน",
        accessor: "startDate",
        render: (item) => (
          <div>
            <div className="text-nowrap">วันที่เริ่มต้น : {dayjs(item.startDate).format("YYYY-MM-DD")}</div>
            <div className="text-nowrap">วันที่สิ้นสุด : {dayjs(item.endDate).format("YYYY-MM-DD")}</div>
          </div>
        ),
      },
      {
        header: "URL",
        accessor: "contentUrl",
        render: (item) => (
          <div
            className="url-container position-relative"
            style={{
              cursor: "pointer",
              maxWidth: "200px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            onClick={() => {
              navigator.clipboard.writeText(item.contentUrl);
              // TODO: Add toast notification for "Copied!"
            }}
          >
            <span>{item.contentUrl}</span>
          </div>
        ),
      },
      {
        header: "ไฮไลท์",
        accessor: "isHighlight",
        align: "center",
        render: (item) => (
          <div className="form-switch">
            <Input type="switch" role="switch" defaultChecked={item.isHighlight}
            onChange={(e) => {
              onStatusChange(item.id, e.target.checked);
              const formData = new FormData();
              formData.append('is_highlight', e.target.checked ? "true" : "false");
              console.log(item.id);
              updateContent({
                id: item.id,
                formData: formData
              })
            }
          } />
          </div>
        ),
      },
      {
        header: "",
        accessor: "actions",
        width: 50,
        render: (item) => (
          <Dropdown
            size="thin"
            isOpen={actionsDropdown[item.id]}
            toggle={() =>
              setActionsDropdown({
                ...actionsDropdown,
                [item.id]: !actionsDropdown[item.id],
              })
            }
          >
            <DropdownToggle tag="span" className="cursor-pointer">
              <i className="mdi mdi-dots-vertical" />
            </DropdownToggle>
            <DropdownMenu>
              <Link to={`/contents/form/${item.id}`} className="text-primary" onClick={(event) => event.stopPropagation()}>
                <DropdownItem>
                  <img src={edit} alt="แก้ไข" />
                  แก้ไขข้อมูล
                </DropdownItem>
              </Link>
              <DropdownItem onClick={() => handleDeleteContents(item)}>
                <img src={trash} />
                ลบโปรโมชัน
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        ),
      },
    ],
    [actionsDropdown, setActionsDropdown, data]
  );

  const handleDeleteContents = async (item) => {
    const result = await modal.alert.danger({
      title: "คุณยืนยันที่จะลบโปรโมชันนี้หรือไม่?",
      message: `ตรวจสอบรายละเอียดโปรโมชันที่คุณต้องการลบออกจากระบบว่าถูกต้องหรือไม่? เมื่อลบแล้ว รายการโปรโมชันที่ถูกลบจะไม่สามารถเรียกกลับมาได้`,
      confirmText: "ลบโปรโมชัน",
      cancelText: "ยกเลิก",
      children: (
        <div className="content-info d-flex align-items-center gap-3">
          <Image
            src={getImageUrl(item.contentImg, {
              type: ImageSourceType.LOCAL,
              path: "contents",
            })}
            alt={item.contentName}
          />
          <div className="text-lg fw-semibold">{item.contentName}</div>
        </div>
      ),
    });
    if (result) {
      try {
        await deleteContent(item.id);
        const toastId = addToast(<ToastNotification variant="success" icon={check} title={`ลบโปรโมชันออกจากระบบเรียบร้อยแล้ว`} description={`โปรโมชัน "${item.contentName}" ถูกลบออกจากระบบเรียบร้อยแล้ว`} onSecondaryClick={() => removeToast(toastId)} />, {
          autoDismiss: true,
        });
        // Refresh data
        if (onTableChange) {
          onTableChange({});
        }
      } catch (error) {
        console.error("Failed to delete content:", error);
        const toastId = addToast(<ToastNotification variant="danger" icon={slash} title="เกิดข้อผิดพลาด" description="ไม่สามารถลบโปรโมชันได้ กรุณาลองใหม่อีกครั้ง" onSecondaryClick={() => removeToast(toastId)} />, {
          autoDismiss: true,
        });
      }
    }
  };

  return (
    <div data-testid="content-table">
      <div className="text-lg mb-4">รายการโปรโมชันที่มีในระบบทั้งหมด {data?.pagination?.total?.toLocaleString() || 0} รายการ *</div>

      <CustomTable keyField="id" data={data?.data || []} columns={columns} itemsPerPage={data?.pagination?.limit || 10} totalItems={data?.pagination?.total || 0} onSort={handleSort} onFilter={handleFilter} onSearch={handleSearch} onPageChange={handlePageChange} status={isLoading ? "loading" : error ? "error" : data?.data?.length === 0 ? "empty" : "normal"} errorMessage={error?.message || "เกิดข้อผิดพลาดในการโหลดข้อมูล"} emptyMessage="ไม่พบข้อมูลโปรโมชัน" />
    </div>
  );
};

const withYupValidation = (WrappedComponent, schema) => {
  return function WithYupValidation(props) {
    // Skip validation if loading or error
    if (props.isLoading || props.error) {
      return <WrappedComponent {...props} />;
    }

    try {
      schema.validateSync(props.data);
      return <WrappedComponent {...props} />;
    } catch (error) {
      return (
        <div className="alert alert-danger">
          <strong>Validation Error:</strong> {error.message}
        </div>
      );
    }
  };
};

const ValidatedTable = withYupValidation(ContentsTable, ContentsSchema);

export default ValidatedTable;
